import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { HelpOutline as HelpOutlineIcon } from '@mui/icons-material';
import '../styles/WaitlistForm.css';
import { getAllUTMData } from '../utils/utmTracker';
import { hasAnalyticsConsent } from '../utils/consent';

// Google Analytics event tracking
const trackEvent = (action, label, value) => {
  if (!hasAnalyticsConsent() || typeof window === 'undefined' || !window.gtag) {
    return;
  }

  window.gtag('event', action, {
    event_category: 'Calculator',
    event_label: label,
    value: value
  });
};

// Tooltip Component
const Tooltip = ({ children, content }) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="tooltip-wrapper">
      <div 
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onFocus={() => setIsVisible(true)}
        onBlur={() => setIsVisible(false)}
      >
        {children}
      </div>
      {isVisible && (
        <div className="tooltip-popup">
          {content}
        </div>
      )}
    </div>
  );
};

// HELPER FUNCTIONS (defined outside the component to prevent re-creation on re-renders)

function calculateEstimatedRate(creditScore) {
  const score = parseInt(creditScore) || 700;
  
  if (score >= 760) return 6.25;
  if (score >= 720) return 6.50;
  if (score >= 680) return 6.75;
  if (score >= 640) return 7.25;
  return 7.75;
}

function calculateMaxPrice(data, customizeExpenses, includeHoa) {
  const annualIncome = parseFloat(data.annualIncome) || 0;
  const monthlyDebt = parseFloat(data.monthlyDebt) || 0;
  const downPayment = parseFloat(data.downPayment) || 0;
  const creditScore = parseInt(data.creditScore) || 700;
  const hasRateQuote = data.hasRateQuote || false;
  const interestRate = hasRateQuote ? (parseFloat(data.manualRate) || 6.5) : calculateEstimatedRate(creditScore);
  const loanTerm = parseFloat(data.loanTerm) || 30;

  if (annualIncome === 0) return 0;

  const monthlyIncome = annualIncome / 12;
  let maxDTI = 0.43;
  if (creditScore >= 760) maxDTI = 0.45;
  else if (creditScore >= 660) maxDTI = 0.41;
  else if (creditScore < 660) maxDTI = 0.39;

  const maxMonthlyPayment = (monthlyIncome * maxDTI) - monthlyDebt;

  if (maxMonthlyPayment <= 0) return 0;

  const calculateMaxLoan = (availableForPI) => {
    const monthlyRate = interestRate / 100 / 12;
    const numPayments = loanTerm * 12;
    if (monthlyRate <= 0) return Infinity;
    return availableForPI * ((Math.pow(1 + monthlyRate, numPayments) - 1) / (monthlyRate * Math.pow(1 + monthlyRate, numPayments)));
  };

  if (customizeExpenses) {
    const propertyTaxes = parseFloat(data.propertyTaxes) || 0;
    const insurance = parseFloat(data.insurance) || 0;
    const hoaFees = parseFloat(data.hoaFees) || 0;
    const utilities = parseFloat(data.utilities) || 0;
    const totalMonthlyExpenses = propertyTaxes + insurance + hoaFees + utilities;
    const availableForPI = maxMonthlyPayment - totalMonthlyExpenses;
    return availableForPI > 0 ? calculateMaxLoan(availableForPI) + downPayment : 0;
  } else {
    let estimatedMaxPrice = calculateMaxLoan(maxMonthlyPayment) + downPayment;
    for (let i = 0; i < 5; i++) { // Iterate to get a stable estimate
      const estimatedTaxes = (estimatedMaxPrice * 0.012) / 12;
      const estimatedInsurance = (estimatedMaxPrice * 0.005) / 12;
      const estimatedUtilities = (estimatedMaxPrice * 0.006) / 12;
      const estimatedHoa = includeHoa ? (estimatedMaxPrice * 0.004) / 12 : 0;
      const totalMonthlyExpenses = estimatedTaxes + estimatedInsurance + estimatedUtilities + estimatedHoa;
      const availableForPI = maxMonthlyPayment - totalMonthlyExpenses;
      if (availableForPI <= 0) {
        estimatedMaxPrice = 0;
        break;
      }
      estimatedMaxPrice = calculateMaxLoan(availableForPI) + downPayment;
    }
    return estimatedMaxPrice;
  }
}

function getFinalResults(formData, customizeExpenses, includeHoa) {
  const maxPrice = calculateMaxPrice(formData, customizeExpenses, includeHoa);
  const { annualIncome, monthlyDebt, creditScore, downPayment, loanTerm } = formData;
  
  let expenses;
  if (customizeExpenses) {
    expenses = {
      propertyTaxes: parseFloat(formData.propertyTaxes) || 0,
      insurance: parseFloat(formData.insurance) || 0,
      hoaFees: parseFloat(formData.hoaFees) || 0,
      utilities: parseFloat(formData.utilities) || 0,
      wereEstimated: false
    }
  } else {
    expenses = {
      propertyTaxes: (maxPrice * 0.012) / 12,
      insurance: (maxPrice * 0.005) / 12,
      utilities: (maxPrice * 0.006) / 12,
      hoaFees: includeHoa ? (maxPrice * 0.004) / 12 : 0,
      wereEstimated: true
    }
  }
  
  const monthlyIncome = (parseFloat(annualIncome) || 0) / 12;
  const maxLoanAmount = maxPrice > 0 ? maxPrice - (parseFloat(downPayment) || 0) : 0;
  const hasRateQuote = formData.hasRateQuote || false;
  const interestRate = hasRateQuote ? (parseFloat(formData.manualRate) || 6.5) : calculateEstimatedRate(creditScore);
  const monthlyRate = interestRate / 100 / 12;
  const numPayments = (parseFloat(loanTerm) || 30) * 12;

  const principalInterest = maxLoanAmount > 0 ? (maxLoanAmount * monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1) : 0;
  const monthlyPayment = principalInterest + expenses.propertyTaxes + expenses.insurance + expenses.hoaFees + expenses.utilities;

  return {
    maxHomePrice: maxPrice,
    monthlyPayment,
    maxLoanAmount,
    downPayment: parseFloat(downPayment) || 0,
    principalInterest,
    ...expenses,
    totalMonthlyIncome: monthlyIncome,
    totalMonthlyDebt: parseFloat(monthlyDebt) || 0,
    dtiRatio: monthlyIncome > 0 ? ((monthlyPayment + (parseFloat(monthlyDebt) || 0)) / monthlyIncome) * 100 : 0,
    creditScore: parseInt(creditScore) || 700,
    interestRate: interestRate,
    rateSource: hasRateQuote ? 'Manual Quote' : 'Auto-Estimated'
  };
}


const ToggleSwitch = ({ label, isToggled, onToggle }) => (
  <div className="toggle-switch-row">
    <label className="toggle-switch">
      <input type="checkbox" checked={isToggled} onChange={onToggle} />
      <span className="slider"></span>
    </label>
    <span className="toggle-label">{label}</span>
  </div>
);


const AffordabilityCalculator = () => {
  const navigate = useNavigate();
  const [customizeExpenses, setCustomizeExpenses] = useState(false);
  const [includeHoa, setIncludeHoa] = useState(false);

  const [formData, setFormData] = useState({
    annualIncome: '',
    monthlyDebt: '',
    creditScore: '700',
    downPayment: '',
    hasRateQuote: false,
    manualRate: '',
    loanTerm: '30',
    propertyTaxes: '',
    insurance: '',
    hoaFees: '',
    utilities: ''
  });

  const estimatedMaxHomePrice = useMemo(() => {
    return calculateMaxPrice(formData, customizeExpenses, includeHoa);
  }, [formData, customizeExpenses, includeHoa]);

  const downPaymentPercentage = useMemo(() => {
    if (estimatedMaxHomePrice > 0 && formData.downPayment) {
      return ((parseFloat(formData.downPayment) / estimatedMaxHomePrice) * 100).toFixed(1);
    }
    return '';
  }, [formData.downPayment, estimatedMaxHomePrice]);

  const currentInterestRate = useMemo(() => {
    if (formData.hasRateQuote) {
      return parseFloat(formData.manualRate) || 0;
    } else {
      return calculateEstimatedRate(formData.creditScore);
    }
  }, [formData.hasRateQuote, formData.manualRate, formData.creditScore]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Track calculator submission with UTM data
    const utmData = getAllUTMData();
    trackEvent('calculator_submit', 'affordability_calculation', 1);
    
    const finalResults = getFinalResults(formData, customizeExpenses, includeHoa);
    
    // Include UTM data in the results state
    navigate('/calculator/results', { 
      state: { 
        results: finalResults,
        utmData 
      } 
    });
  };

  const handleToggleChange = (toggleType, newValue) => {
    trackEvent('toggle_change', toggleType, newValue ? 1 : 0);
    
    if (toggleType === 'customize_expenses') {
      setCustomizeExpenses(newValue);
    } else if (toggleType === 'include_hoa') {
      setIncludeHoa(newValue);
    } else if (toggleType === 'has_rate_quote') {
      setFormData(prev => ({ ...prev, hasRateQuote: newValue }));
    }
  };

  return (
    <div className="affordability-container">
      <form onSubmit={handleSubmit} className="affordability-form">
        <div className="form-section">
          <h3 className="section-title">Income & Debts</h3>
          <div className="affordability-row">
            <label>Annual Pre-tax Income (USD)</label>
            <input 
              type="number" 
              value={formData.annualIncome} 
              onChange={e => handleInputChange('annualIncome', e.target.value)}
              placeholder="75000"
              required 
            />
          </div>
          <div className="affordability-row">
            <label>Monthly Debt Payments (USD)</label>
            <input 
              type="number" 
              value={formData.monthlyDebt} 
              onChange={e => handleInputChange('monthlyDebt', e.target.value)}
              placeholder="500"
            />
            <small className="input-help">Include car loans, student loans, credit cards, etc.</small>
          </div>
        </div>

        <div className="form-section">
          <h3 className="section-title">Credit & Down Payment</h3>
          <div className="affordability-row">
            <label>Credit Score</label>
            <input 
              type="number" 
              value={formData.creditScore} 
              onChange={e => handleInputChange('creditScore', e.target.value)}
              min="300"
              max="850"
              required
            />
            <small className="input-help">Score between 300-850</small>
          </div>
          <div className="affordability-row">
            <label>Down Payment Amount (USD)</label>
            <input 
              type="number" 
              value={formData.downPayment} 
              onChange={e => handleInputChange('downPayment', e.target.value)}
              placeholder="50000"
              required
            />
            {downPaymentPercentage > 0 && estimatedMaxHomePrice > 0 && (
              <small className="input-help">
                ~{downPaymentPercentage}% of an estimated {formatCurrency(estimatedMaxHomePrice)} home price
              </small>
            )}
          </div>
        </div>

        <div className="form-section">
          <h3 className="section-title">Loan Details</h3>
          <div className="affordability-row">
            <label>Interest Rate</label>
            <ToggleSwitch
              label="I've already received a rate quote"
              isToggled={formData.hasRateQuote}
              onToggle={() => handleToggleChange('has_rate_quote', !formData.hasRateQuote)}
            />
          </div>
          
          <div className="affordability-row">
            {formData.hasRateQuote ? (
              <>
                <label>Your Rate Quote (%)</label>
                <input 
                  type="number" 
                  step="0.01" 
                  value={formData.manualRate} 
                  onChange={e => handleInputChange('manualRate', e.target.value)}
                  placeholder="6.50"
                  required
                />
                <small className="input-help">Enter the rate you received from your lender</small>
              </>
            ) : (
              <>
                <label>Estimated Interest Rate (%)</label>
                <div className="rate-display">
                  <span className="estimated-rate">{currentInterestRate.toFixed(2)}%</span>
                  <small className="input-help">
                    Based on your credit score of {formData.creditScore}
                  </small>
                </div>
              </>
            )}
          </div>
          
          <div className="affordability-row">
            <label>Loan Term (Years)</label>
            <select 
              value={formData.loanTerm} 
              onChange={e => handleInputChange('loanTerm', e.target.value)}
              className="loan-term-select"
            >
              <option value="30">30 Years</option>
              <option value="15">15 Years</option>
            </select>
          </div>
        </div>

        <div className="form-section">
          <h3 className="section-title">Monthly Expenses</h3>
          <ToggleSwitch
            label="Customize monthly expenses"
            isToggled={customizeExpenses}
            onToggle={() => handleToggleChange('customize_expenses', !customizeExpenses)}
          />

          {!customizeExpenses && (
            <div className="hoa-toggle-container">
              <ToggleSwitch
                label="Include potential HOA fees in estimate"
                isToggled={includeHoa}
                onToggle={() => handleToggleChange('include_hoa', !includeHoa)}
              />
            </div>
          )}

          {customizeExpenses && (
            <div className="custom-expenses-inputs">
              <div className="affordability-row">
                <label>Property Taxes (USD/month)</label>
                <input 
                  type="number" 
                  value={formData.propertyTaxes} 
                  onChange={e => handleInputChange('propertyTaxes', e.target.value)}
                  placeholder="400"
                />
              </div>
              <div className="affordability-row">
                <label>Homeowners Insurance (USD/month)</label>
                <input 
                  type="number" 
                  value={formData.insurance} 
                  onChange={e => handleInputChange('insurance', e.target.value)}
                  placeholder="150"
                />
              </div>
              <div className="affordability-row">
                <div className="label-with-tooltip">
                  <label>HOA Fees (USD/month)</label>
                  <Tooltip content="HOA fees are typically monthly or annual payments made to a homeowners association (HOA) for the maintenance and upkeep of common areas, such as landscaping, pools, and community facilities. These fees can significantly impact your monthly housing costs and overall affordability. Including them in your estimate can provide a more accurate picture of your total monthly expenses and the maximum home price you can afford.">
                    <button 
                      type="button"
                      className="tooltip-trigger"
                    >
                      <HelpOutlineIcon fontSize="small" />
                    </button>
                  </Tooltip>
                </div>
                <input 
                  type="number" 
                  value={formData.hoaFees} 
                  onChange={e => handleInputChange('hoaFees', e.target.value)}
                  placeholder="0"
                />
              </div>
              <div className="affordability-row">
                <label>Estimated Utilities (USD/month)</label>
                <input 
                  type="number" 
                  value={formData.utilities} 
                  onChange={e => handleInputChange('utilities', e.target.value)}
                  placeholder="200"
                />
              </div>
            </div>
          )}
        </div>

        <button type="submit" className="affordability-submit">
          Calculate Affordability
        </button>
      </form>
    </div>
  );
};

export default AffordabilityCalculator; 
